import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

const ITEM_CLASS =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 font-mono text-[10px] uppercase tracking-[0.2em] tabular-nums transition-colors';
const INACTIVE_CLASS =
  'border-border/60 text-muted-foreground hover:border-foreground/40 hover:text-foreground';
const ACTIVE_CLASS = 'border-heat/60 text-heat';
const ARROW_CLASS = 'disabled:pointer-events-none disabled:opacity-40';

// Returns the page numbers to render, with `gap-<n>` markers where pages are skipped.
function pageWindow(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const candidates = new Set([1, pageCount, page, page - 1, page + 1]);
  const pages = [...candidates].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);

  const items = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) items.push(`gap-${p}`);
    items.push(p);
  });
  return items;
}

const Pagination = ({ page, pageCount, onPageChange, className }) => {
  if (!pageCount || pageCount <= 1) return null;

  function goToPage(target) {
    onPageChange(Math.min(pageCount, Math.max(1, target)));
  }

  return (
    <nav
      aria-label="Pagination"
      className={cn('mt-8 flex flex-wrap items-center justify-center gap-2', className)}
    >
      <button
        type="button"
        onClick={() => goToPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={cn(ITEM_CLASS, INACTIVE_CLASS, ARROW_CLASS)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>

      {pageWindow(page, pageCount).map((item) => {
        if (typeof item !== 'number') {
          return (
            <span key={item} className="px-1 font-mono text-[10px] text-muted-foreground/60">
              …
            </span>
          );
        }

        const isCurrent = item === page;
        return (
          <button
            key={item}
            type="button"
            onClick={() => goToPage(item)}
            aria-current={isCurrent ? 'page' : undefined}
            className={cn(ITEM_CLASS, isCurrent ? ACTIVE_CLASS : INACTIVE_CLASS)}
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => goToPage(page + 1)}
        disabled={page >= pageCount}
        aria-label="Next page"
        className={cn(ITEM_CLASS, INACTIVE_CLASS, ARROW_CLASS)}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </nav>
  );
};

export default Pagination;
